import type { ReactNode } from 'react';

export interface PageHeaderProps {
  /** Page title (rendered as the page's h1). */
  title: string;
  /** Optional one-line subtitle beneath the title. */
  subtitle?: string;
  /**
   * Optional right-aligned header actions (e.g. the Referrals Export button).
   * Rendered opposite the title within the band.
   */
  actions?: ReactNode;
}

/**
 * The canonical page-header band (SCRUM-663), shared by every top-level page so
 * headers stay consistent app-wide.
 *
 * Ports the design's `.page-header` (app-shell 7b.html): a full-bleed band with
 * `background: var(--bg-1)`, a bottom border (`var(--border-0)`), and
 * `padding: sp-xl sp-2xl` (24px / 32px → `py-6 px-8`); the title is
 * `font-display 1.625rem/600` with `-0.01em` tracking, and the subtitle is
 * `font-body 0.8125rem` in `text-2` with a small top margin.
 *
 * The band is a sibling ABOVE each page's padded content column (directly under
 * the app-shell `<main>`, which has no padding) so it renders edge-to-edge.
 * `shrink-0` keeps it from compressing inside a full-height `flex-col` page
 * (e.g. Ask BankIQ, whose thread column flexes).
 *
 * Before this, every page hand-rolled its header, which drifted: only Dashboard
 * and Referrals used the band; others used a bare inline title or off-spec sizes.
 * All top-level pages now render their header through this component.
 */
export function PageHeader({ title, subtitle, actions }: PageHeaderProps) {
  return (
    <header className="border-border-0 bg-bg-1 flex shrink-0 items-start justify-between border-b px-8 py-6">
      <div>
        <h1 className="font-display text-text-0 text-[1.625rem] font-semibold tracking-[-0.01em]">
          {title}
        </h1>
        {subtitle ? <p className="text-text-2 mt-1 text-[0.8125rem]">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
    </header>
  );
}
